import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
};

export function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get("/notifications?limit=20").then(({ data }) => { setItems(data.notifications); setUnread(data.unread_count); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, load]);

  if (!user) return null;

  const open = async (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
      setUnread((u) => Math.max(0, u - 1));
      api.post("/notifications/read", { ids: [n.id] }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };
  const readAll = () => {
    api.post("/notifications/read", { all: true }).then(() => { setItems((p) => p.map((i) => ({ ...i, read: true }))); setUnread(0); }).catch(() => {});
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" aria-label="Notifications" className="relative grid place-items-center w-10 h-10 rounded-full border border-border bg-white hover:border-coral transition-colors">
          <Bell size={18} className="text-ink" />
          {unread > 0 && <span data-testid="notifications-unread" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white text-[10px] font-black grid place-items-center">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] max-w-[92vw] p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-heading font-black text-ink">Notifications</p>
          {unread > 0 && <button onClick={readAll} data-testid="notifications-read-all" className="inline-flex items-center gap-1 text-xs font-bold text-coral min-h-[32px]"><Check size={13} /> Mark all read</button>}
        </div>
        <div className="max-h-[380px] overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-sm text-ink-soft text-center">You're all caught up.</p>
          ) : items.map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notification-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors ${n.read ? "" : "bg-coral/5"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-coral shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-ink leading-snug">{n.title}</p>
                  {n.body && <p className="text-xs text-ink-soft mt-0.5 line-clamp-2">{n.body}</p>}
                </div>
                <span className="text-[11px] text-ink-soft shrink-0">{timeAgo(n.created_at)}</span>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
